import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Menu } from 'primereact/menu';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from 'xlsx';
import { getItem } from '../../../utils/localStorage';
import { makeRequest2 } from '../../../utils/makeRequest';
import { transformEmail } from '../../../utils/transformEmail';
import {
  get_all_handovers,
  get_handover,
  update_handover,
  finishDeactivationUrl,
  backend_url,
  public_handover_pdf,
  send_handover_email,
  send_handover_sms
} from '../../../utils/urls';
import { toastify } from '../../../utils/toast';
import Layout from '../component/layout';
// import CompareHandovers from './compare_handover';
import EditHandover from './update_handover';
import HandoverDetailsDialog from './HandoverDetailsDialog';

const HandoverManagement = () => {
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'moveIn');
  const [handovers, setHandovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewDetailsDialog, setViewDetailsDialog] = useState(false);
  const [selectedHandover, setSelectedHandover] = useState(null);
  const [handoverDetailsLoading, setHandoverDetailsLoading] = useState(false);
  const [deactivationSuccess, setDeactivationSuccess] = useState(false);
  const [deactivatedUnit, setDeactivatedUnit] = useState(null);

  // State for popup management
  const [showCompareDialog, setShowCompareDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedHandoverId, setSelectedHandoverId] = useState(null);
  const [showCompletionWarning, setShowCompletionWarning] = useState(false);
  const [handoverToComplete, setHandoverToComplete] = useState(null);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: null,
    startDate: null,
    endDate: null
  });

  // Status options for dropdown
  const statusOptions = [
    { label: 'All Statuses', value: null },
    { label: 'Draft', value: 'Draft' },
    { label: 'Completed', value: 'Completed' },
    { label: 'Disputed', value: 'Disputed' },
    { label: 'Resolved', value: 'Resolved' }
  ];

  // Pagination
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  const fetchHandovers = useCallback(async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const facilityId = await getItem("selectedFacilityId");
      if (!facilityId) {
        toastify("No facility selected", "error");
        return;
      }
      console.log('Facility ID from localStorage:', facilityId);

      // Make a simple request without query parameters first
      const response = await makeRequest2(`${get_all_handovers}/${facilityId}`, "GET");
      console.log('API Response:', response);

      if (response?.success && response.data) {
        console.log('Full response data:', response.data);

        // Extract handovers from response
        let allHandovers = [];

        // Try to handle different possible response formats
        if (Array.isArray(response.data)) {
          allHandovers = response.data;
        } else if (response.data.handovers && Array.isArray(response.data.handovers)) {
          allHandovers = response.data.handovers;
        } else if (typeof response.data === 'object') {
          // Try to find any arrays in the response
          Object.keys(response.data).forEach(key => {
            if (Array.isArray(response.data[key])) {
              allHandovers = [...allHandovers, ...response.data[key]];
            }
          });
        }

        // Filter handovers based on active tab
        const filteredHandovers = allHandovers.filter(
          handover => handover.handoverType === (activeTab === 'moveIn' ? 'MoveIn' : 'MoveOut')
        );

        console.log(`Filtered ${activeTab} handovers:`, filteredHandovers.length);

        if (filteredHandovers.length > 0) {
          console.log('Sample handover:', filteredHandovers[0]);
        }

        // Set filtered handovers to state
        setHandovers(filteredHandovers || []);
        setTotalRecords(filteredHandovers?.length || 0);
      } else {
        toastify(`Error: ${response?.error || 'Could not fetch handovers'}`, 'error');
        setHandovers([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error('Exception when fetching handovers:', error);
      toastify(`Error: ${error.message || 'Could not fetch handovers'}`, 'error');
      setHandovers([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  const handleCompleteHandover = (handover) => {
    console.log('Completing handover:', handover);

    // For move-out handovers, show warning first
    if (handover.handoverType === 'MoveOut') {
      setHandoverToComplete(handover);
      setShowCompletionWarning(true);
    } else {
      // For move-in handovers, complete directly
      updateHandoverStatus(handover._id, 'Completed');
    }
  };

  const updateHandoverStatus = async (handoverId, status, isDeactivationNeeded = false, handoverData = null) => {
    try {
      setLoading(true);
      const facilityId = await getItem("selectedFacilityId");

      if (!facilityId) {
        toastify("No facility selected", "error");
        return;
      }

      // Update the handover status first
      const updateUrl = `${update_handover}/${facilityId}/${handoverId}`;

      console.log('Making request to update handover status:', updateUrl);

      const payload = {
        status: status
      };

      const response = await makeRequest2(updateUrl, "PUT", payload);

      if (!response?.success) {
        throw new Error(response?.error || 'Could not update handover status');
      }

      toastify(`Handover marked as ${status} successfully`, 'success');

      // If this is a move-out handover being completed, deactivate the customer from the unit
      if (status === 'Completed' && isDeactivationNeeded && handoverData) {
        try {
          // Extract the unit and customer IDs
          const unitId = typeof handoverData.unitId === 'object' ?
            handoverData.unitId._id : handoverData.unitId;

          const customerId = typeof handoverData.customerId === 'object' ?
            handoverData.customerId._id : handoverData.customerId;

          if (!unitId || !customerId) {
            console.error('Missing unit or customer ID in handover data');
            toastify('Warning: Could not remove customer from unit due to missing data', 'warning');
            return;
          }

          // Get display names for messaging
          const customerName = typeof handoverData.customerId === 'object' ?
            `${handoverData.customerId.firstName || ''} ${handoverData.customerId.lastName || ''}`.trim() :
            'customer';

          const unitName = typeof handoverData.unitId === 'object' ?
            handoverData.unitId.name || 'the unit' :
            'the unit';

          // Call the deactivation endpoint to remove the customer from the unit
          console.log(`Removing customer ${customerId} from unit ${unitId}`);

          const deactivationUrl = `${finishDeactivationUrl}/${facilityId}/${unitId}`;
          console.log('Deactivation URL:', deactivationUrl);

          const deactivationResponse = await makeRequest2(
            deactivationUrl,
            "POST",
            { customerId }
          );

          if (deactivationResponse?.success) {
            console.log('Customer removed from unit successfully:', deactivationResponse);
            setDeactivationSuccess(true);
            setDeactivatedUnit(unitName);

            // Show success message
            toastify(`Customer "${customerName}" has been removed from unit "${unitName}"`, 'success');
          } else {
            console.error('Error removing customer from unit:', deactivationResponse?.error);
            setDeactivationSuccess(true); // Still show success dialog with warning
            setDeactivatedUnit(unitName);

            // Show warning message
            toastify(`Warning: Error removing customer from unit. You may need to do this manually. Error: ${deactivationResponse?.error || 'Unknown error'}`, 'warning', 6000);
          }
        } catch (error) {
          console.error('Exception during customer removal:', error);
          toastify(`Warning: Error removing customer from unit: ${error.message}. Please do this manually.`, 'warning', 6000);
        }
      }

      // Refresh handovers data
      fetchHandovers(currentPage, rows);

    } catch (error) {
      console.error('Exception when updating handover status:', error);
      toastify(`Error: ${error.message || 'Could not update handover status'}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Also update the handler to pass the handover data
  const handleCompletionConfirm = () => {
    if (handoverToComplete) {
      // Pass the complete handover object
      updateHandoverStatus(handoverToComplete._id, 'Completed', true, handoverToComplete);
    }
    setShowCompletionWarning(false);
    setHandoverToComplete(null);
  };

  // Add this function to handle cancellation from the warning dialog
  const handleCompletionCancel = () => {
    setShowCompletionWarning(false);
    setHandoverToComplete(null);
  };

  // Reset pagination when tab changes
  useEffect(() => {
    console.log('Tab changed to:', activeTab);
    setFirst(0);
    setCurrentPage(1);
    fetchHandovers(1, rows);
  }, [activeTab, fetchHandovers, rows]);

  // Handle filter changes
  useEffect(() => {
    // Debounced fetch to avoid too many requests
    const handler = setTimeout(() => {
      setFirst(0);
      setCurrentPage(1);
      fetchHandovers(1, rows);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm, filters, fetchHandovers, rows]);

  useEffect(() => {
    console.log("Current handovers data count:", handovers?.length || 0);
    console.log("Total records:", totalRecords);
  }, [handovers, totalRecords]);

  const onPageChange = (event) => {
    if (!event) return;

    setFirst(event.first || 0);
    const page = Math.floor((event.first || 0) / (event.rows || 10)) + 1;
    setCurrentPage(page);
    setRows(event.rows || 10);
  };

  const handleViewDetails = async (handover) => {
    console.log('Viewing details for handover:', handover._id);
    setViewDetailsDialog(true);
    setSelectedHandover(null);
    setHandoverDetailsLoading(true);
    try {
      const facilityId = await getItem("selectedFacilityId");
      if (!facilityId) {
        toastify("No facility selected", "error");
        return;
      }
      const response = await makeRequest2(`${get_handover}/${facilityId}/${handover._id}`, "GET");
      if (response?.success && response.data?.data) {
        setSelectedHandover(response.data.data);
      } else {
        toastify(response?.error || "Could not fetch handover details", "error");
        setSelectedHandover(handover);
      }
    } catch (error) {
      toastify(error.message || "Could not fetch handover details", "error");
      setSelectedHandover(handover);
    } finally {
      setHandoverDetailsLoading(false);
    }
  };

  // Updated to use popup dialog instead of navigation
  const handleEditHandover = (handover) => {
    console.log('Editing handover:', handover._id);
    setSelectedHandover(handover);
    setSelectedHandoverId(handover._id);
    setShowEditDialog(true);
  };

  // Updated to use popup dialog instead of navigation
  // const handleCompareHandovers = (handover) => {
  //   console.log('Comparing handover with move-in handover:', handover._id);
  //   setSelectedHandover(handover);
  //   setSelectedHandoverId(handover._id);
  //   setShowCompareDialog(true);
  // };

  // Callback for when dialogs are closed - refreshes data
  const handleDialogClose = () => {
    // Refresh data when dialog is closed
    fetchHandovers(currentPage, rows);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (e) {
      console.error('Error formatting date:', dateString, e);
      return 'Invalid Date';
    }
  };

  const statusBodyTemplate = (rowData) => {
    const statusClass = {
      'Draft': 'bg-warning',
      'Completed': 'bg-success',
      'Disputed': 'bg-danger',
      'Resolved': 'bg-info'
    };
  
    return (
      <span className={`badge ${statusClass[rowData.status] || 'bg-secondary'} text-white`}>
        {rowData.status || 'Unknown'}
      </span>
    );
  };

  const getExportUnitName = (handover) => {
    if (handover?.unitId && typeof handover.unitId === 'object' && handover.unitId.name) {
      return handover.unitId.name;
    }
    return 'Not available';
  };

  const getExportCustomerName = (handover) => {
    if (handover?.customerId && typeof handover.customerId === 'object') {
      const name = [handover.customerId.firstName, handover.customerId.lastName].filter(Boolean).join(' ').trim();
      return name || handover.customerId.fullName || 'Not available';
    }
    return 'Not available';
  };

  const buildExportRows = () => {
    return (handovers || []).map((handover) => ({
      Unit: getExportUnitName(handover),
      Customer: getExportCustomerName(handover),
      Type: handover?.handoverType || 'Not available',
      'Handover Date': formatDate(handover?.handoverDate),
      Status: handover?.status || 'Not available'
    }));
  };

  const exportCSV = () => {
    if (!handovers || handovers.length === 0) {
      toastify('No handovers available to export', 'info');
      return;
    }

    const exportDate = new Date().toLocaleDateString().replace(/\//g, '-');
    const sheet = XLSX.utils.json_to_sheet(buildExportRows());
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Handovers');
    XLSX.writeFile(workbook, `handovers_${activeTab}_${exportDate}.csv`, { bookType: 'csv' });
  };

  const exportXLS = () => {
    if (!handovers || handovers.length === 0) {
      toastify('No handovers available to export', 'info');
      return;
    }

    const exportDate = new Date().toLocaleDateString().replace(/\//g, '-');
    const sheet = XLSX.utils.json_to_sheet(buildExportRows());
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Handovers');
    XLSX.writeFile(workbook, `handovers_${activeTab}_${exportDate}.xlsx`);
  };

  const exportPDF = () => {
    if (!handovers || handovers.length === 0) {
      toastify('No handovers available to export', 'info');
      return;
    }

    const exportDate = new Date().toLocaleDateString();
    const doc = new jsPDF();
    const title = `Handover Management (${activeTab === 'moveIn' ? 'Move-In' : 'Move-Out'})`;

    doc.setFontSize(14);
    doc.text(title, 14, 20);
    doc.setFontSize(10);
    doc.text(`Date: ${exportDate}`, 14, 28);

    const rows = buildExportRows();
    autoTable(doc, {
      startY: 36,
      head: [['Unit', 'Customer', 'Type', 'Handover Date', 'Status']],
      body: rows.map((row) => [row.Unit, row.Customer, row.Type, row['Handover Date'], row.Status]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [52, 58, 64] }
    });

    doc.save(`handovers_${activeTab}_${exportDate.replace(/\//g, '-')}.pdf`);
  };

  const sendHandoverToClient = async (handover) => {
    try {
      const facilityId = await getItem('selectedFacilityId');
      if (!facilityId) {
        toastify('No facility selected', 'error');
        return;
      }

      const response = await makeRequest2(
        `${send_handover_email}/${facilityId}/${handover._id}`,
        'POST',
        {}
      );

      if (response?.success) {
        const recipient = response.recipient ? transformEmail(response.recipient) : 'customer';
        toastify(`Handover sent to ${recipient}`, 'success');
      } else {
        toastify(response?.error || 'Failed to send handover email', 'error');
      }
    } catch (error) {
      console.error('Failed to send handover email:', error);
      toastify(error.message || 'Failed to send handover email', 'error');
    }
  };

  const downloadHandoverPdf = async (handover) => {
    try {
      const facilityId = await getItem('selectedFacilityId');
      if (!facilityId) {
        toastify('No facility selected', 'error');
        return;
      }

      // Open the PDF in a new tab for download
      const pdfUrl = `${backend_url}${public_handover_pdf}/${facilityId}/${handover._id}`;
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('Failed to download handover PDF:', error);
      toastify(error.message || 'Failed to download handover PDF', 'error');
    }
  };

  const sendHandoverSms = async (handover) => {
    try {
      const facilityId = await getItem('selectedFacilityId');
      if (!facilityId) {
        toastify('No facility selected', 'error');
        return;
      }

      const response = await makeRequest2(
        `${send_handover_sms}/${facilityId}/${handover._id}`,
        'POST',
        {}
      );

      if (response?.success) {
        toastify(`Handover SMS sent to ${response.recipient}`, 'success');
      } else {
        toastify(response?.error || 'Failed to send handover SMS', 'error');
      }
    } catch (error) {
      console.error('Failed to send handover SMS:', error);
      toastify(error.message || 'Failed to send handover SMS', 'error');
    }
  };

  // Action Cell Component to properly use hooks
  const ActionCell = ({ rowData }) => {
    const menuRef = useRef(null);

    const menuItems = [
      {
        label: 'Send via Email',
        icon: 'ti ti-mail',
        command: () => sendHandoverToClient(rowData)
      },
      {
        label: 'Download Handover',
        icon: 'ti ti-download',
        command: () => downloadHandoverPdf(rowData)
      },
      {
        label: 'Send via SMS',
        icon: 'ti ti-message',
        command: () => sendHandoverSms(rowData)
      }
    ];

    return (
      <div className="d-flex gap-2 align-items-center">
        <Button
          icon="ti ti-eye"
          className="p-button-sm p-button-info"
          tooltip="View Details"
          onClick={() => handleViewDetails(rowData)}
        />
        <Button
          icon="ti ti-pencil"
          className="p-button-sm p-button-warning"
          tooltip="Edit"
          onClick={() => handleEditHandover(rowData)}
        />
        {rowData.status !== 'Completed' && (
          <Button
            icon="ti ti-check"
            className="p-button-sm p-button-success"
            tooltip="Mark as Completed"
            onClick={() => handleCompleteHandover(rowData)}
          />
        )}
        <Menu model={menuItems} popup ref={menuRef} />
        <Button
          icon="ti ti-dots-vertical"
          className="p-button-sm p-button-secondary"
          tooltip="More Actions"
          onClick={(e) => menuRef.current.toggle(e)}
        />
      </div>
    );
  };

  const actionBodyTemplate = (rowData) => {
    return <ActionCell rowData={rowData} />;
  };

  const renderCompletionWarningDialog = () => {
    return (
      <Dialog
        header="Warning: Completion Will Remove Tenant"
        visible={showCompletionWarning}
        style={{ width: '50vw', maxWidth: '600px' }}
        onHide={handleCompletionCancel}
        modal
        footer={
          <div className="text-end">
            <Button
              label="Cancel"
              icon="ti ti-x"
              className="p-button-text me-2"
              onClick={handleCompletionCancel}
            />
            <Button
              label="Confirm Completion"
              icon="ti ti-check"
              className="p-button-danger"
              onClick={handleCompletionConfirm}
            />
          </div>
        }
      >
        <div className="p-3">
          <div className="d-flex align-items-center mb-3">
            <i className="ti ti-alert-triangle text-warning me-2" style={{ fontSize: '2rem' }}></i>
            <h5 className="mb-0">This action cannot be undone!</h5>
          </div>
          <p>
            You are about to mark this move-out handover as <strong>Completed</strong>. This will:
          </p>
          <ul className="mb-3">
            <li>Permanently remove the tenant from this unit</li>
            <li>Mark the unit as vacant and available for new tenants</li>
            <li>Finalize all security deposit deductions</li>
          </ul>
          <p className="mb-0 font-medium">
            Please confirm that all information is correct and you want to proceed.
          </p>
        </div>
      </Dialog>
    );
  };

  const renderDeactivationSuccessDialog = () => {
    return (
      <Dialog
        header="Move-Out Completed Successfully"
        visible={deactivationSuccess}
        style={{ width: '40vw', maxWidth: '500px' }}
        onHide={() => setDeactivationSuccess(false)}
        modal
        footer={
          <div className="text-end">
            <Button
              label="OK"
              icon="ti ti-check"
              onClick={() => setDeactivationSuccess(false)}
            />
          </div>
        }
      >
        <div className="p-3 text-center">
          <div className="mb-4">
            <i className="ti ti-circle-check text-success" style={{ fontSize: '3rem' }}></i>
          </div>
          <h5 className="mb-3">Move-Out Handover Completed</h5>
          <p>
            The move-out handover for <strong>{deactivatedUnit || 'the unit'}</strong> has been successfully completed.
          </p>
          <div className="alert alert-success mt-3">
            <i className="ti ti-check-circle me-2"></i>
            <strong>Customer Removal:</strong> The system has attempted to automatically remove the customer from this unit.
          </div>
          <div className="alert alert-info mt-3">
            <i className="ti ti-info-circle me-2"></i>
            <strong>Verification Recommended:</strong> Please verify in the Unit Management section that the customer has been successfully removed from the unit.
          </div>
          <p className="text-muted small mt-3">
            The customer account remains in the system for record-keeping purposes, but should no longer be associated with this unit.
          </p>
        </div>
      </Dialog>
    );
  };


  const customerTemplate = (rowData) => {
    if (!rowData.customerId) {
      console.log('Missing customerId for row:', rowData._id);
      return 'Unknown Customer';
    }

    // Check if customerId is already an object with firstName/lastName
    const firstName = typeof rowData.customerId === 'object' ? (rowData.customerId.firstName || '') : '';
    const lastName = typeof rowData.customerId === 'object' ? (rowData.customerId.lastName || '') : '';

    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    } else if (typeof rowData.customerId === 'object') {
      console.log('CustomerId is object but missing name fields:', rowData.customerId);
    } else {
      console.log('CustomerId not in expected format:', rowData.customerId);
    }

    return 'Unknown Customer';
  };

  const unitTemplate = (rowData) => {
    if (!rowData.unitId) {
      console.log('Missing unitId for row:', rowData._id);
      return 'Unknown Unit';
    }

    // Check if unitId is already an object with name
    if (typeof rowData.unitId === 'object' && rowData.unitId.name) {
      const floorUnitNo = rowData.unitId.floorUnitNo ? ` (${rowData.unitId.floorUnitNo})` : '';
      return `${rowData.unitId.name}${floorUnitNo}`;
    } else if (typeof rowData.unitId === 'object') {
      console.log('UnitId is object but missing name:', rowData.unitId);
    } else {
      console.log('UnitId not in expected format:', rowData.unitId);
    }

    return 'Unknown Unit';
  };

  // Details dialog is now extracted to HandoverDetailsDialog component

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/app/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="">Property Management</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="">Handover Management</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="m-0">Handover Management</h5>
          <Link
            to={`/facility/handover_management/add_${activeTab === 'moveIn' ? 'move_in' : 'move_out'}_handover`}
            className="btn btn-primary btn-sm"
          >
            <i className="ti ti-plus me-2"></i> Add {activeTab === 'moveIn' ? 'Move-In' : 'Move-Out'} Handover
          </Link>
        </div>
        <div className="card-body py-0">
          <ul className="nav nav-tabs profile-tabs" role="tablist">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'moveIn' ? 'active' : ''}`}
                onClick={() => setActiveTab('moveIn')}
                role="tab"
                type="button"
              >
                <i className="ti ti-login me-2"></i>Move-In Handovers
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'moveOut' ? 'active' : ''}`}
                onClick={() => setActiveTab('moveOut')}
                role="tab"
                type="button"
              >
                <i className="ti ti-logout me-2"></i>Move-Out Handovers
              </button>
            </li>
          </ul>

          <div className="tab-content">
            <div className={`tab-pane ${activeTab === 'moveIn' || activeTab === 'moveOut' ? 'show active' : ''}`}>
              <div className="d-flex flex-wrap gap-2 justify-content-end mb-3">
                <Button type="button" label="CSV" rounded onClick={exportCSV} data-pr-tooltip="CSV" />
                <Button type="button" label="XLS" severity="success" rounded onClick={exportXLS} data-pr-tooltip="XLS" />
                <Button type="button" label="PDF" severity="warning" rounded onClick={exportPDF} data-pr-tooltip="PDF" />
              </div>

              <DataTable
                value={handovers || []}
                emptyMessage={`No ${activeTab === 'moveIn' ? 'move-in' : 'move-out'} handovers found.`}
                stripedRows
                loading={loading}
                tableStyle={{ minWidth: '50rem' }}
                paginator
                first={first}
                rows={rows}
                totalRecords={totalRecords}
                onPage={onPageChange}
                rowsPerPageOptions={[10, 25, 50]}
              >
                <Column field="unitId.name" header="Unit" body={unitTemplate} sortable></Column>
                <Column
                  field="customerId"
                  header="Customer"
                  body={customerTemplate}
                  sortable
                ></Column>
                <Column
                  field="handoverDate"
                  header="Handover Date"
                  body={(rowData) => formatDate(rowData.handoverDate)}
                  sortable
                ></Column>
                <Column
                  field="status"
                  header="Status"
                  body={statusBodyTemplate}
                  sortable
                ></Column>
                <Column body={actionBodyTemplate} header="Actions" style={{ width: '15rem' }}></Column>
              </DataTable>
            </div>
          </div>
        </div>
      </div>

      {/* Handover Details Dialog */}
      <HandoverDetailsDialog
        visible={viewDetailsDialog}
        onHide={() => setViewDetailsDialog(false)}
        handover={selectedHandover}
        loading={handoverDetailsLoading}
      />

      {/* Render Compare Handovers popup */}
      {/* {showCompareDialog && selectedHandover && (
        <CompareHandovers 
          id={selectedHandoverId} 
          location={{ state: { handover: selectedHandover } }}
          onClose={() => {
            setShowCompareDialog(false);
            handleDialogClose();
          }}
        />
      )} */}

      {/* Render Edit Handover popup */}
      {showEditDialog && selectedHandover && (
        <EditHandover
          id={selectedHandoverId}
          location={{ state: { handover: selectedHandover } }}
          onClose={() => {
            setShowEditDialog(false);
            handleDialogClose();
          }}
        />
      )}
      {renderCompletionWarningDialog()}
      {renderDeactivationSuccessDialog()}
    </Layout>
  );
};

export default HandoverManagement;
